import React, { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { emailCustomFieldService } from '@/services/emailCustomFieldService'

const fieldSchema = z.object({
  name: z.string().min(1, 'Field key is required').regex(/^[a-z0-9_]+$/, 'Lowercase letters, numbers and underscores only'),
  label: z.string().min(1, 'Label is required'),
  field_type: z.string(),
  is_required: z.boolean(),
  select_options: z.string().optional(),
})

const CustomFieldFormDialog = ({ open, onOpenChange, field, onSuccess }) => {
  const isEditMode = !!field

  const form = useForm({
    resolver: zodResolver(fieldSchema),
    defaultValues: { name: '', label: '', field_type: 'TEXT', is_required: false, select_options: '' },
  })

  useEffect(() => {
    if (!open) return
    if (isEditMode && field) {
      form.reset({
        name: field.name || '',
        label: field.label || '',
        field_type: field.field_type || 'TEXT',
        is_required: !!field.is_required,
        select_options: (field.select_options || []).join(', '),
      })
    } else {
      form.reset({ name: '', label: '', field_type: 'TEXT', is_required: false, select_options: '' })
    }
  }, [open, isEditMode, field, form])

  const fieldType = form.watch('field_type')

  const onSubmit = async (values) => {
    try {
      const payload = {
        label: values.label,
        field_type: values.field_type,
        is_required: values.is_required,
        select_options: values.field_type === 'SELECT'
          ? (values.select_options || '').split(',').map((s) => s.trim()).filter(Boolean)
          : [],
      }
      if (!isEditMode) payload.name = values.name

      if (isEditMode) {
        await emailCustomFieldService.updateField(field.id, payload)
        toast.success('Custom field updated')
      } else {
        await emailCustomFieldService.createField(payload)
        toast.success('Custom field created')
      }
      onSuccess?.()
      onOpenChange(false)
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save custom field')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Custom Field' : 'New Custom Field'}</DialogTitle>
          <DialogDescription>Custom fields can be used for segmentation and merge tags.</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field: f }) => (
                <FormItem>
                  <FormLabel>Field Key</FormLabel>
                  <FormControl>
                    <Input placeholder="loyalty_tier" disabled={isEditMode} {...f} />
                  </FormControl>
                  <FormDescription>Used in merge tags, e.g. {'{{'}{f.value || 'field_key'}{'}}'}. Cannot be changed later.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="label"
              render={({ field: f }) => (
                <FormItem>
                  <FormLabel>Display Label</FormLabel>
                  <FormControl>
                    <Input placeholder="Loyalty Tier" {...f} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="field_type"
              render={({ field: f }) => (
                <FormItem>
                  <FormLabel>Type</FormLabel>
                  <Select onValueChange={f.onChange} value={f.value} disabled={isEditMode}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="TEXT">Text</SelectItem>
                      <SelectItem value="NUMBER">Number</SelectItem>
                      <SelectItem value="DATE">Date</SelectItem>
                      <SelectItem value="BOOLEAN">Yes / No</SelectItem>
                      <SelectItem value="SELECT">Dropdown</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
            {fieldType === 'SELECT' && (
              <FormField
                control={form.control}
                name="select_options"
                render={({ field: f }) => (
                  <FormItem>
                    <FormLabel>Options (comma-separated)</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Bronze, Silver, Gold" {...f} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
            <FormField
              control={form.control}
              name="is_required"
              render={({ field: f }) => (
                <FormItem className="flex items-center gap-2 space-y-0">
                  <FormControl>
                    <Checkbox checked={f.value} onCheckedChange={f.onChange} />
                  </FormControl>
                  <FormLabel className="!mt-0">Required field</FormLabel>
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit">
                {isEditMode ? 'Save Changes' : 'Create Field'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export default CustomFieldFormDialog
